import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, getErrorMessage } from '../../api/client'
import { useOfflineFirstQuery } from '../../hooks/useOfflineFirstQuery'
import { useOnlineStatus } from '../../hooks/useOnlineStatus'
import { resolveTenantSlug } from '../../lib/tenant'
import { useAuthStore } from '../../stores/authStore'

type SessionDetail = {
  id: number
  client: number
  client_name: string
  client_mrn: string
  authorization: number
  authorization_number: string
  service_date: string
  service_code: string
  modifiers: string[]
  scheduled_start: string
  scheduled_end: string
  actual_start: string | null
  actual_end: string | null
  status: string
  status_display: string
  cancellation_reason: string | null
  cancellation_by: string | null
  rendering_provider_name: string
  supervising_provider_name: string | null
  place_of_service_display: string
  service_location_address: string | null
  billable_units: string | null
  non_billable_minutes: number
  duration_minutes: number
  billable_minutes: number
  diagnosis_code: string
  is_billable: boolean
  billing_status: string
  can_generate_claim: { can_bill: boolean; reason: string }
  note_id: number | null
  note_status: string
  created_at: string
  updated_at: string
}

type TreatmentPlanListItem = {
  id: number
  client: number
  status: string
  effective_date: string
  version_number: number
}

export default function SessionDetailPage() {
  const tenantSlug = resolveTenantSlug()
  const isOnline = useOnlineStatus()
  const user = useAuthStore((s) => s.user)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const params = useParams()
  const sessionId = Number(params.sessionId)

  const [clockOutNonBillable, setClockOutNonBillable] = useState<number>(0)
  const [clockOutOverride, setClockOutOverride] = useState(false)
  const [clockOutOverrideReason, setClockOutOverrideReason] = useState('')

  const cacheKey = useMemo(() => {
    return `tenant:${tenantSlug ?? 'unknown'}:sessions:detail:${sessionId}`
  }, [sessionId, tenantSlug])

  const sessionQuery = useOfflineFirstQuery<SessionDetail>({
    queryKey: ['session', sessionId],
    cacheKey,
    cacheMaxAgeMs: 2 * 60 * 1000,
    enabled: Number.isFinite(sessionId) && sessionId > 0,
    queryFn: async () => {
      const res = await apiClient.get<SessionDetail>(`/api/v1/sessions/${sessionId}/`)
      return res.data
    },
  })

  const clockInMutation = useMutation({
    mutationFn: async () => {
      await apiClient.post(`/api/v1/sessions/${sessionId}/clock_in/`, {})
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['session', sessionId] })
      await queryClient.invalidateQueries({ queryKey: ['sessions'] })
    },
  })

  const clockOutMutation = useMutation({
    mutationFn: async () => {
      await apiClient.post(`/api/v1/sessions/${sessionId}/clock_out/`, {
        non_billable_minutes: clockOutNonBillable,
        override: clockOutOverride,
        override_reason: clockOutOverride ? clockOutOverrideReason : undefined,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['session', sessionId] })
      await queryClient.invalidateQueries({ queryKey: ['sessions'] })
    },
  })

  const createNoteMutation = useMutation({
    mutationFn: async () => {
      const session = sessionQuery.data
      if (!session) throw new Error('Session not loaded.')

      const plansRes = await apiClient.get<{ results: TreatmentPlanListItem[] }>(
        '/api/v1/treatment-plans/',
        { params: { client: session.client, status: 'active' } }
      )
      const activePlans = plansRes.data?.results ?? []
      if (activePlans.length === 0) {
        throw new Error('No active treatment plan found for this client.')
      }

      const plan = activePlans.sort((a, b) => b.effective_date.localeCompare(a.effective_date))[0]
      const noteRes = await apiClient.post('/api/v1/clinical-notes/', {
        session: session.id,
        treatment_plan: plan.id,
        session_narrative: '',
        client_response: '',
        caregiver_involvement: '',
        interventions_used: '',
        next_session_plan: '',
        medical_necessity_statement: '',
        behavior_data_summary: '',
        supervision_provided: false,
        supervision_type: '',
        supervision_notes: '',
        goals_addressed: [],
      })
      return noteRes.data as { id: number }
    },
    onSuccess: async (note) => {
      await queryClient.invalidateQueries({ queryKey: ['session', sessionId] })
      navigate(`/app/notes/${note.id}`)
    },
  })

  if (!Number.isFinite(sessionId) || sessionId <= 0) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        Invalid session id.
      </div>
    )
  }

  const session = sessionQuery.data
  const canOverrideClockOut = user?.role === 'admin' || user?.role === 'bcba'

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Session</h1>
          <p className="text-sm text-gray-600">
            {session ? (
              <>
                <Link to={`/app/clients/${session.client}`} className="font-medium text-primary-600 hover:text-primary-700">
                  {session.client_name}
                </Link>{' '}
                | {session.service_date} | {session.service_code}
              </>
            ) : (
              'Loading session...'
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/app/sessions" className="btn-secondary">
            Back
          </Link>
          {session?.note_id ? (
            <Link to={`/app/notes/${session.note_id}`} className="btn-primary">
              Open note
            </Link>
          ) : (
            <button
              type="button"
              className="btn-primary"
              disabled={!isOnline || createNoteMutation.isPending || sessionQuery.isLoading}
              onClick={() => createNoteMutation.mutate()}
            >
              {createNoteMutation.isPending ? 'Creating note...' : 'Start note'}
            </button>
          )}
        </div>
      </div>

      {(sessionQuery.isError || clockInMutation.isError || clockOutMutation.isError || createNoteMutation.isError) && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {getErrorMessage(sessionQuery.error) ||
            getErrorMessage(clockInMutation.error) ||
            getErrorMessage(clockOutMutation.error) ||
            getErrorMessage(createNoteMutation.error)}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Session Detail</h2>

            {sessionQuery.isLoading && !session ? (
              <div className="space-y-3">
                <div className="h-4 w-96 bg-gray-200 rounded animate-pulse" />
                <div className="h-4 w-80 bg-gray-200 rounded animate-pulse" />
                <div className="h-4 w-72 bg-gray-200 rounded animate-pulse" />
              </div>
            ) : session ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-500">Status</div>
                  <div className="text-gray-900">{session.status_display ?? session.status}</div>
                </div>
                <div>
                  <div className="text-gray-500">Place of service</div>
                  <div className="text-gray-900">{session.place_of_service_display}</div>
                </div>
                <div>
                  <div className="text-gray-500">Scheduled</div>
                  <div className="text-gray-900">
                    {session.scheduled_start} - {session.scheduled_end}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Actual</div>
                  <div className="text-gray-900">
                    {session.actual_start ?? '-'} - {session.actual_end ?? '-'}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Authorization</div>
                  <div className="text-gray-900">{session.authorization_number}</div>
                </div>
                <div>
                  <div className="text-gray-500">Provider</div>
                  <div className="text-gray-900">{session.rendering_provider_name}</div>
                </div>
                <div>
                  <div className="text-gray-500">Billable units</div>
                  <div className="text-gray-900">{session.billable_units ?? '-'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Billing status</div>
                  <div className="text-gray-900">{session.billing_status}</div>
                </div>
                <div className="md:col-span-2">
                  <div className="text-gray-500">Can generate claim</div>
                  <div className="text-gray-900">
                    {session.can_generate_claim.can_bill ? 'Yes' : `No (${session.can_generate_claim.reason})`}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-600">No session data.</div>
            )}
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Clock Actions</h2>
            {!isOnline && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 mb-4">
                Offline: clock actions require connectivity.
              </div>
            )}

            <div className="flex flex-col md:flex-row gap-3">
              <button
                type="button"
                className="btn-primary"
                disabled={!isOnline || clockInMutation.isPending || !session}
                onClick={() => clockInMutation.mutate()}
              >
                {clockInMutation.isPending ? 'Clocking in...' : 'Clock in'}
              </button>

              <button
                type="button"
                className="btn-primary"
                disabled={!isOnline || clockOutMutation.isPending || !session}
                onClick={() => clockOutMutation.mutate()}
              >
                {clockOutMutation.isPending ? 'Clocking out...' : 'Clock out'}
              </button>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label" htmlFor="non_billable_minutes">
                  Non-billable minutes
                </label>
                <input
                  id="non_billable_minutes"
                  type="number"
                  min={0}
                  className="input mt-1"
                  value={clockOutNonBillable}
                  onChange={(e) => setClockOutNonBillable(Number(e.target.value))}
                />
              </div>

              <div>
                <label className="label" htmlFor="override">
                  Override (admin/BCBA)
                </label>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    id="override"
                    type="checkbox"
                    checked={clockOutOverride}
                    disabled={!canOverrideClockOut}
                    onChange={(e) => setClockOutOverride(e.target.checked)}
                  />
                  <span className="text-sm text-gray-700">Allow override</span>
                </div>
              </div>

              {clockOutOverride && (
                <div className="md:col-span-2">
                  <label className="label" htmlFor="override_reason">
                    Override reason
                  </label>
                  <input
                    id="override_reason"
                    className="input mt-1"
                    value={clockOutOverrideReason}
                    onChange={(e) => setClockOutOverrideReason(e.target.value)}
                    disabled={!canOverrideClockOut}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Clinical Note</h2>
            {session ? (
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Status</span>
                  <span className="font-medium text-gray-900">{session.note_status}</span>
                </div>
                {session.note_id ? (
                  <Link to={`/app/notes/${session.note_id}`} className="btn-secondary w-full text-center">
                    Open note
                  </Link>
                ) : (
                  <button
                    type="button"
                    className="btn-secondary w-full"
                    disabled={!isOnline || createNoteMutation.isPending}
                    onClick={() => createNoteMutation.mutate()}
                  >
                    {createNoteMutation.isPending ? 'Creating...' : 'Create note'}
                  </button>
                )}
              </div>
            ) : (
              <div className="text-sm text-gray-600">No note information.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

